import React, { useState } from "react";
import { Modal } from "newui/components/ui/Modal";

interface InviteUserConfirmModalProps {
  isOpen: boolean;
  email: string;
  requiresOverrideReason: boolean;
  isLoading?: boolean;
  onConfirm: (overrideReason?: string) => void;
  onClose: () => void;
}

export const InviteUserConfirmModal: React.FC<InviteUserConfirmModalProps> = ({
  isOpen,
  email,
  requiresOverrideReason,
  isLoading = false,
  onConfirm,
  onClose,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const overrideReasonValid =
    !requiresOverrideReason ||
    (overrideReason.trim().length >= 10 && overrideReason.trim().length <= 500);

  const canConfirm = confirmed && overrideReasonValid && !isLoading;

  const handleClose = () => {
    setConfirmed(false);
    setOverrideReason("");
    onClose();
  };

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }
    onConfirm(requiresOverrideReason ? overrideReason.trim() : undefined);
  };

  return (
    <Modal
      isOpen={isOpen}
      modalType="alert"
      iconType="mail"
      title="Send invite?"
      primaryButton={{
        text: "Send invite",
        disabled: !canConfirm,
        isLoading,
        onClick: handleConfirm,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: handleClose,
      }}
      onClose={handleClose}
    >
      <div className="space-y-4 text-sm text-gray-700">
        <p>
          An invite will be sent to{" "}
          <strong data-testid="confirm-modal-email">{email}</strong>. The invite
          link expires after <strong>72 hours</strong>.
        </p>

        {requiresOverrideReason && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-amber-800 font-medium text-xs mb-2">
              Domain override required
            </p>
            <label
              htmlFor="confirm-override-reason"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Reason for override <span className="text-red-500">*</span>
            </label>
            <textarea
              id="confirm-override-reason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Explain why this user should be invited…"
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              data-testid="confirm-override-reason-textarea"
            />
            <div className="flex justify-between mt-0.5">
              <span className="text-xs text-gray-400">Min 10 characters</span>
              <span className="text-xs text-gray-400">
                {overrideReason.length}/500
              </span>
            </div>
          </div>
        )}

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
            data-testid="confirm-invite-checkbox"
          />
          <span>
            I confirm this is a valid Procuracon user and the invite should be
            sent to this email address.
          </span>
        </label>
      </div>
    </Modal>
  );
};
